import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { IRemunerationFixed } from 'interfaces/RemunerationFixed';
import {
  IMyKnownError,
  REQUEST_LIFE_CICLE,
  ICreateRequestPayload,
} from 'interfaces/requests';

import { get, post, patch, MICROSERVICES } from 'api';
import { AppDispatch, store } from 'store';
import { normalizeAndRemoveFields } from 'utils/files';
import { openAlert } from 'modules/alertHandler';

import endpoints from './endpoints';
import { IRemunerationFixedState } from './interfaces';
import { setSelectedRemunerationModeAction } from 'modules/remunerationModeNavigation/actions';
import { listRemunerationFixedAction } from 'modules/remunerationFixedList';
import { REMUNERATION_FORM_MODE } from 'modules/remunerationModeNavigation/constants';
import { parseToInt } from 'utils/texts';
import { remunerationFieldsToRemove } from 'utils/remuneration';

export const showRemunerationFixedAction = createAsyncThunk<
  IRemunerationFixed,
  string,
  { rejectValue: IMyKnownError }
>('showRemunerationFixed', async (payload, { rejectWithValue, dispatch }) => {
  try {
    const result = await get<IRemunerationFixed>(MICROSERVICES.STRATEGIES, {
      url: endpoints.showRemunerationFixed(payload),
    });
    const remuneration = {
      ...result.data,
      id: result.data._id,
    };
    dispatch(
      setSelectedRemunerationModeAction(REMUNERATION_FORM_MODE.viewFixed),
    );
    return remuneration;
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Erro ao tentar visualizar a Remuneração Fixa',
      }),
    );
    return rejectWithValue(error.response.data.message);
  }
});

export const createRemunerationFixedAction = createAsyncThunk<
  void,
  ICreateRequestPayload<IRemunerationFixed>,
  { rejectValue: IMyKnownError; dispatch: AppDispatch }
>(
  'createRemunerationFixed',
  async ({ item, resetFormSubmitting }, { rejectWithValue, dispatch }) => {
    const {
      fixedIndicatorFieldNamesByType,
      fixedIndicatorFields,
    } = store.getState().parameters.list;

    const currentFieldsOfFixedIndicator = fixedIndicatorFieldNamesByType[
      item.fixedIndicator
    ].join(' ');
    const fieldsToRemove = fixedIndicatorFields.filter(
      (field) =>
        item[field] <= 0 || !currentFieldsOfFixedIndicator.match(field),
    );

    const periodicityMonthSpecification = parseToInt(
      item.periodicityMonthSpecification,
    );
    const parsedRemunerationFixed = normalizeAndRemoveFields(
      Object.assign({}, { ...item, periodicityMonthSpecification }),
      remunerationFieldsToRemove(item.periodicityType, fieldsToRemove),
    );
    try {
      await post<IRemunerationFixed>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createRemunerationsFixed(),
        data: parsedRemunerationFixed,
      });
      resetFormSubmitting();
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração criado com sucesso!',
          primaryButtonAction: setSelectedRemunerationModeAction(
            REMUNERATION_FORM_MODE.list,
          ),
        }),
      );
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Remuneração Fixa com esse nome.'
        : 'Erro para criar uma Remuneração Fixa';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const inactivateRemunerationFixedAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>(
  'inactivateRemunerationFixed',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await patch<unknown>(MICROSERVICES.STRATEGIES, {
        url: endpoints.inactivateRemunerationFixed(payload),
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração inativado com sucesso!',
          primaryButtonAction: listRemunerationFixedAction({}),
        }),
      );
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: error.response.data.message,
        }),
      );
      return rejectWithValue(error.response.data.message);
    }
  },
);

const initialState: IRemunerationFixedState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  submitting: REQUEST_LIFE_CICLE.IDLE,
  remunerationFixed: {} as IRemunerationFixed,
  errorMessage: '',
};

const remunerationFixedSlice = createSlice({
  name: 'remunerationFixed',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(showRemunerationFixedAction.fulfilled, (state, action) => {
      state.errorMessage = '';
      state.remunerationFixed = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(showRemunerationFixedAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(showRemunerationFixedAction.rejected, (state, action) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(createRemunerationFixedAction.fulfilled, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(createRemunerationFixedAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(createRemunerationFixedAction.rejected, (state, action) => {
      state.submitting = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(inactivateRemunerationFixedAction.fulfilled, () => {
      return initialState;
    });
    builder.addCase(inactivateRemunerationFixedAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      inactivateRemunerationFixedAction.rejected,
      (state, action) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
  },
});

const { reducer, actions } = remunerationFixedSlice;
export const { reset } = actions;
export default reducer;
